import { useEffect } from 'react'

import { useDispatch, useStore } from 'react-redux'

import {
  clearFFOverrides,
  type FeatureFlagKey,
  type FeatureFlags,
  writeFFOverride,
} from '@webapp/core/services/FeatureFlag'
import {
  makeSelectFeatureFlags,
  updateFlag,
  updateFlags,
} from '@webapp/core/stores/global'
import { updateHasFFOverride } from './stores/actions'
import { makeSelectLiveFFState } from './stores/selectors'

interface ProcurifyDevConsoleApi {
  getFlags: () => FeatureFlags
  getFlag: (key: FeatureFlagKey) => boolean
  setFlag: (key: FeatureFlagKey, value: boolean) => void
  clearFlagOverrides: () => void
}

declare global {
  interface Window {
    __procurifyDev?: ProcurifyDevConsoleApi
  }
}

/**
 * Dev-only (mounted behind IS_DEV). Exposes a feature-flag console API for
 * humans and browser agents at window.__procurifyDev. Overrides persist in
 * sessionStorage and are re-applied on boot by the dev saga, which must be
 * injected before the app's initial UPDATE_FEATURE_FLAGS dispatch (see the
 * ProcurifyDevToolsLoadable mount point in App).
 */
export const useDevFeatureFlagConsoleApi = () => {
  const store = useStore()
  const dispatch = useDispatch()

  useEffect(() => {
    const getFlags = () => makeSelectFeatureFlags()(store.getState())

    window.__procurifyDev = {
      getFlags,
      getFlag: (key) => Boolean(getFlags()[key]),
      setFlag: (key, value) => {
        writeFFOverride(key, value)
        dispatch(updateFlag(key, value))
        dispatch(updateHasFFOverride(true))
      },
      clearFlagOverrides: () => {
        clearFFOverrides()
        dispatch(updateHasFFOverride(false))
        dispatch(updateFlags(makeSelectLiveFFState()(store.getState())))
      },
    }

    return () => {
      delete window.__procurifyDev
    }
  }, [])
}
